/**
 * CERTIFICATE — the credential that carries its own evidence.
 *
 * FOUNDER, 2026-08-15: "build p2 and p4".
 *
 * ── IT SHOWS THE STANDARD, NOT A SEAL ─────────────────────────────────────────────────────────
 * An ordinary certificate asserts. This one prints what was required, item by item, and the command
 * that re-runs the check. A reader who does not trust the issuer does not have to: they can read
 * the standard and run it. That is FP-018 applied to a credential — compute, quote, or null.
 *
 * ── IT WILL NOT ISSUE FOR READING ─────────────────────────────────────────────────────────────
 * There is no button here that produces a certificate. It watches the project and the audit, and
 * appears when a published check passes. A learner who has read every chapter and produced nothing
 * sees exactly what is still open, by name.
 *
 * ── A REVOKED ONE STAYS ON SCREEN ─────────────────────────────────────────────────────────────
 * The ledger is append-only and the history is rendered. A credential that could quietly vanish
 * would be evidence of nothing, including the parts that were true.
 */
import { useState } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import MANIFEST from '../../project-manifest.json';
import SUBJECTS from '../../audit-subjects.json';
import { checkArtefact, ladderFor } from './projectspine.mjs';
import { checkAudit, subjects } from './auditspine.mjs';
import { issue, append, revoke, standingOf, WHY_NOT_AN_ASSERTION, WHY_APPEND_ONLY, NOT_ISSUED_ON_READING } from './certificate.mjs';

const P_STORE = 'ankr-academy-project';
const A_STORE = 'ankr-academy-audit';
const L_STORE = 'ankr-academy-certificates';
const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || '{}'); } catch { return {}; } };
const readL = () => { try { return JSON.parse(localStorage.getItem(L_STORE) || '[]'); } catch { return []; } };
const saveL = (l) => { try { localStorage.setItem(L_STORE, JSON.stringify(l)); } catch { /* ignore */ } };

export default function CertLab({ level = 'CODEAI101', principal = 'you' }) {
  const [ledger, setLedger] = useState(() => readL());
  const project = read(P_STORE);
  const audits = read(A_STORE);
  const today = new Date().toISOString().slice(0, 10);

  const pCheck = checkArtefact(MANIFEST, level, project);
  const subj = subjects(SUBJECTS)[0];
  const aCheck = checkAudit(audits[subj?.id] || {});

  const claims = [
    { kind: 'project', check: pCheck, artefact: ladderFor(MANIFEST, level)?.artefact, standard: 'gate-projectspine.mjs', what: 'Your own project' },
    { kind: 'audit', check: aCheck, artefact: subj ? `an audit of ${subj.name}` : null, standard: 'gate-auditspine.mjs', what: 'A product audit' },
  ];

  const doIssue = (c) => {
    const r = issue({ kind: c.kind, principal, level, artefact: c.artefact, check: c.check, standardName: c.standard, on: today });
    if (!r.ok) return;
    const next = append(ledger, { event: 'issued', on: today, certificate: r.certificate });
    setLedger(next); saveL(next);
  };
  const doRevoke = () => {
    const r = revoke(ledger, { certificateOf: principal, reason: 'withdrawn by the holder', on: today });
    if (r.ok) { setLedger(r.ledger); saveL(r.ledger); }
  };

  const standing = standingOf(ledger, principal);

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Certificate · evidence, not a seal</div>
      <h2 className="g-h2 lab-h">What you built, and the check that passed</h2>
      <p className="g-body">{WHY_NOT_AN_ASSERTION}</p>

      {/* ── WHAT IS CERTIFIABLE ── */}
      <div className="lab-task">
        <div className="g-label">What you have produced</div>
        {claims.map((c) => (
          <div key={c.kind} className={`crt-claim ${c.check.passes ? 'crt-ready' : ''}`}>
            <div className="aud-head">
              <b>{c.what}</b>
              <span className="cou-sp" />
              {c.check.passes
                ? <Value of={computed('check passes')} />
                : <Value of={refused(c.check.verdict)} />}
            </div>
            {!c.check.passes && (
              <ul className="fai-list">
                {[...(c.check.missing || []), ...(c.check.unreasoned || [])].slice(0, 4)
                  .map((m) => <li key={m.id}>{m.asks}</li>)}
              </ul>
            )}
            {c.check.passes && (
              <button className="g-btn g-btn-primary" onClick={() => doIssue(c)}>Issue the certificate</button>
            )}
          </div>
        ))}
        <p className="g-body g-dim">{NOT_ISSUED_ON_READING}</p>
      </div>

      {/* ── THE CERTIFICATE ITSELF ── */}
      <div className="lab-task">
        <div className="g-label">Held</div>
        {!standing.held && standing.status === 'none' && <Value of={absent()} />}
        {standing.status === 'revoked' && (
          <p className="g-body crt-revoked">
            <b>Revoked.</b> The record stays, with its reason — a credential that could quietly
            vanish would be evidence of nothing, including the parts that were true.
          </p>
        )}
        {standing.certificate && (
          <div className={`crt-card ${standing.status === 'revoked' ? 'crt-void' : ''}`}>
            <div className="crt-top">
              <b>{standing.certificate.artefact}</b>
              <span className="prj-where">{standing.certificate.level} · {standing.certificate.kind}</span>
            </div>
            <div className="lab-readout">
              <div><span className="g-label">Requirements met</span>
                <Value of={computed(String(standing.certificate.requirements_met))} /></div>
              <div><span className="g-label">Issued</span>
                <Value of={computed(standing.certificate.issued_on)} /></div>
              <div><span className="g-label">Standing</span>
                <Value of={computed(standing.status)} /></div>
            </div>
            <div className="g-label crt-sh">What was required — printed, not summarised</div>
            <ol className="crt-std">
              {standing.certificate.standard.map((s) => (
                <li key={s.id}>{s.asks}{s.chapter ? <span className="prj-where"> ({s.level} ch{s.chapter})</span> : null}</li>
              ))}
            </ol>
            {standing.certificate.reproduce && (
              <>
                <div className="g-label crt-sh">How to disbelieve it</div>
                <pre className="rag-prompt crt-repro">{standing.certificate.reproduce}</pre>
              </>
            )}
            {standing.held && <button className="g-btn" onClick={doRevoke}>Withdraw it</button>}
          </div>
        )}
        {standing.history.length > 0 && (
          <>
            <div className="g-label crt-sh">The record</div>
            <ul className="fai-list">
              {standing.history.map((h, i) => (
                <li key={i}>{h.on} — {h.event}{h.reason ? ` (${h.reason})` : ''}</li>
              ))}
            </ul>
            <p className="g-body g-dim">{WHY_APPEND_ONLY}</p>
          </>
        )}
      </div>

      <p className="g-body g-dim lab-why">
        <Value of={cited('compute, quote, or null', 'ANKR founding principle FP-018')} /> applied to a
        credential. This one does not say you completed something — it records a check: what was
        required, what you produced, when it passed, and the command that runs it again.
        <br />
        There is no button that issues one without a check. And this stays on your machine; nothing
        here publishes a certificate anywhere.
      </p>
    </div>
  );
}
